import type { CompanySearchResult } from '@dash-lite/sdk';
import { Building2, Lock } from 'lucide-react';
import { Alert, Card, CardBody, CardHeader, DataList, DataRow, Skeleton } from '@/components/ui/primitives';
import { useCompanyPreview } from '@/hooks/use-company-preview';
import { formatCountry, orDash } from '@/lib/format';

/**
 * Aperçu affiché à côté du formulaire de paiement.
 *
 * Il répond à la seule question que se pose le visiteur à cet instant :
 * « est-ce que je paie pour la bonne entreprise ? ». Tout ce qu'il montre
 * vient de l'appel **gratuit et public** `GET /companies/search` — la fiche
 * payante n'est pas demandée, et ne pourrait de toute façon pas l'être : sans
 * droit d'accès, l'API répond `401` ou `403`.
 *
 * Le parti pris de présentation compte autant que le code. Les rubriques que
 * l'accès ouvrira sont **nommées, sans valeur ni faux-semblant** : pas de
 * lignes floutées, pas de vignette de document, pas de contenu qui aurait
 * l'air d'être là et qu'on retiendrait. Ce qui n'est pas affiché n'a pas été
 * envoyé au navigateur, et l'écran ne prétend pas le contraire.
 */
export function CompanyPreview({
    registrationNumber,
    initial = null,
}: {
    registrationNumber: string | null;
    /** Résultat de recherche transporté depuis la liste, s'il existe. */
    initial?: CompanySearchResult | null;
}) {
    const { company, loading, error, unknown } = useCompanyPreview(registrationNumber, initial);

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader
                    title="L'entreprise consultée"
                    description="Informations publiques du registre, affichées avant tout paiement."
                    icon={<Building2 className="size-4" />}
                />

                <CardBody>
                    {loading ? (
                        <div className="space-y-3">
                            {[0, 1, 2, 3].map((row) => (
                                <Skeleton key={row} className="h-5 w-full" />
                            ))}
                        </div>
                    ) : company ? (
                        <DataList>
                            <DataRow label="Dénomination" value={company.name} />
                            <DataRow
                                label="Identifiant national"
                                value={<span className="font-mono">{company.registration_number}</span>}
                            />
                            <DataRow label="Ville" value={orDash(company.city)} />
                            <DataRow label="Forme juridique" value={orDash(company.legal_form)} />
                            <DataRow label="Pays du registre" value={formatCountry(company.country)} />
                        </DataList>
                    ) : (
                        <p className="text-ink-soft text-sm leading-relaxed">
                            {unknown && registrationNumber ? (
                                <>
                                    L'identifiant <span className="font-mono">{registrationNumber}</span> n'a pas été
                                    reconnu par la recherche publique. Vérifiez-le avant de payer : l'accès s'ouvrira
                                    quand même, mais peut-être pas sur l'entreprise que vous cherchez.
                                </>
                            ) : (
                                "Aucune entreprise n'est rattachée à cet achat. Votre accès ouvrira la consultation des fiches du registre, à commencer par celle que vous choisirez ensuite."
                            )}
                        </p>
                    )}

                    {error && (
                        <Alert tone="caution" title="Aperçu indisponible" className="mt-4">
                            {error.message} Le paiement et l'ouverture de l'accès n'en dépendent pas.
                        </Alert>
                    )}
                </CardBody>
            </Card>

            <Card>
                <CardHeader
                    title="Ce que votre accès ouvre"
                    description="Les rubriques de la fiche complète, à leur nom."
                    icon={<Lock className="size-4" />}
                />

                <CardBody>
                    <ul className="text-ink-soft space-y-2 text-sm">
                        {UNLOCKED_SECTIONS.map((section) => (
                            <li key={section} className="flex items-start gap-2.5">
                                <Lock className="text-ink-muted mt-0.5 size-3.5 shrink-0" aria-hidden />
                                {section}
                            </li>
                        ))}
                    </ul>

                    <p className="text-ink-muted border-line mt-5 border-t pt-4 text-xs leading-relaxed">
                        L'aperçu ci-dessus vient de <code className="font-mono">GET /companies/search</code>, public et
                        gratuit. La fiche complète vient de <code className="font-mono">GET /companies/{'{id}'}</code>,
                        réservée aux accès actifs — elle n'est appelée qu'une fois le vôtre ouvert.
                    </p>
                </CardBody>
            </Card>
        </div>
    );
}

/** Les rubriques que le paiement débloque. Aucune valeur n'est affichée. */
const UNLOCKED_SECTIONS = [
    'Capital social',
    'Activité et code d’activité',
    'Dirigeants et dates de nomination',
    'Effectifs et établissements',
    'Greffe et juridiction compétente',
    'Identifiant européen (EUID)',
];
